import type { InternalSupportItem } from "../types/SupportItem.js";

// Single source of truth for the Help Center id. AC#5 (Help Center always
// visible) is enforced by App.ts looking up this id in `defaultItems`; if the
// literal here drifts from the lookup, Help Center silently disappears.
export const HELP_CENTER_ID = "help-center";

// interstate-document-text-icon (viewBox 0 0 16 16)
const HELP_CENTER_SVG = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 16 16" width="16" height="16" fill="currentColor" aria-hidden="true"><path fill-rule="evenodd" d="M3.30405 1.44287C3.19154 1.44287 3.10034 1.53407 3.10034 1.64658V14.3534C3.10034 14.4659 3.19154 14.5571 3.30405 14.5571H12.6961C12.8086 14.5571 12.8998 14.4659 12.8998 14.3534V7.72376C12.8998 6.6959 12.0665 5.86264 11.0386 5.86264H9.93371C9.13085 5.86264 8.48 5.2118 8.48 4.40894V3.30399C8.48 2.27612 7.64675 1.44287 6.61888 1.44287H3.30405ZM3.30405 0.192871C2.50119 0.192871 1.85034 0.843718 1.85034 1.64658V14.3534C1.85034 15.1563 2.50119 15.8071 3.30405 15.8071H12.6961C13.4989 15.8071 14.1498 15.1563 14.1498 14.3534V7.44753C14.1498 3.44089 10.9017 0.192871 6.89511 0.192871H3.30405ZM9.44981 2.01186C9.62973 2.40541 9.73 2.84299 9.73 3.30399V4.40894C9.73 4.52144 9.8212 4.61264 9.93371 4.61264H11.0386C11.4997 4.61264 11.9372 4.71292 12.3308 4.89283C11.7359 3.62927 10.7134 2.60676 9.44981 2.01186ZM4.6127 10.2099C4.6127 9.86471 4.89252 9.58489 5.2377 9.58489H10.7624C11.1076 9.58489 11.3874 9.86471 11.3874 10.2099C11.3874 10.5551 11.1076 10.8349 10.7624 10.8349H5.2377C4.89252 10.8349 4.6127 10.5551 4.6127 10.2099ZM4.6127 12.4198C4.6127 12.0746 4.89252 11.7948 5.2377 11.7948H8.00006C8.34523 11.7948 8.62506 12.0746 8.62506 12.4198C8.62506 12.7649 8.34523 13.0448 8.00006 13.0448H5.2377C4.89252 13.0448 4.6127 12.7649 4.6127 12.4198Z"/></svg>`;

// Inline telephone glyph (viewBox 0 0 16 16, fill=currentColor) — ships with
// the bundle so the support-code row's icon never depends on the Interstate
// icon CDN (`ids-icon` would silently render an empty fallback SVG if its
// slug were unknown). Same render path as HELP_CENTER_SVG: `iconSvg` →
// `_isSafeSvg`-guarded `unsafeHTML` in `SupportMenuItem._renderIconBody`.
const SUPPORT_CODE_SVG = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 16 16" width="16" height="16" fill="currentColor" aria-hidden="true"><path fill-rule="evenodd" d="M1.885.511a1.745 1.745 0 0 1 2.61.163L6.29 2.98c.329.423.445.974.315 1.494l-.547 2.19a.678.678 0 0 0 .178.643l2.457 2.457a.678.678 0 0 0 .644.178l2.189-.547a1.745 1.745 0 0 1 1.494.315l2.306 1.794c.829.645.905 1.87.163 2.611l-1.034 1.034c-.74.74-1.846 1.065-2.877.702a18.634 18.634 0 0 1-7.01-4.42 18.634 18.634 0 0 1-4.42-7.009c-.362-1.03-.037-2.137.703-2.877L1.885.511z"/></svg>`;

export const defaultItems: InternalSupportItem[] = [
  {
    id: HELP_CENTER_ID,
    label: "Help Center",
    subtitle: "Browse articles and guides",
    iconKey: "help",
    iconSvg: HELP_CENTER_SVG,
    href: "https://help.coxautoinc.com",
    target: "_blank",
    analyticsLabel: "help-center",
  },
  {
    id: "support-code",
    label: "Verify Support Code",
    subtitle: "Password and account access",
    // `iconSvg` wins at render — `iconKey` is the documented semantic intent
    // (mirrors `help-center`, which also carries both) but is unreachable in
    // `SupportMenuItem._renderIconBody` as long as `iconSvg` is set + safe.
    iconKey: "phone",
    iconSvg: SUPPORT_CODE_SVG,
    target: "_blank",
    analyticsLabel: "support-code",
    // Activation URLs for the IVR support-code page (US1944211). Resolved per
    // `bb_env` domain family at list-build time via `resolveEnvHref` in
    // build-filtered-items.ts. Absent/unknown `bb_env` → `live` (mirrors the
    // bridge-bar host's own `|| templatesMap.prod` default).
    hrefByEnv: {
      dangerzone:
        "https://authorize.dangerzone.coxautoservices.com/activate?ivr=true",
      live: "https://authorize.coxautoinc.com/activate?ivr=true",
    },
  },
];

// AC#5 (Help Center always visible) depends on a HELP_CENTER_ID entry being
// present in any item list passed in. Fail loud rather than silently dropping
// Help Center if the literal here drifts from the lookup site.
export function assertHelpCenterPresent(
  items: ReadonlyArray<InternalSupportItem>,
): InternalSupportItem {
  const helpCenter = items.find((d) => d.id === HELP_CENTER_ID);
  if (!helpCenter) {
    throw new Error(
      `[support-menu] item list must contain an entry whose id === HELP_CENTER_ID ("${HELP_CENTER_ID}") — AC#5 requires Help Center to always render.`,
    );
  }
  return helpCenter;
}

// Module-load invariant: exercise the guard at import time so a regression
// in defaultItems surfaces immediately, not on first render.
assertHelpCenterPresent(defaultItems);
